import React, {useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import axios from 'axios';
import {DataTable} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {Dialog} from 'primereact/dialog';
import {AutoComplete} from 'primereact/autocomplete';
import {Message} from 'primereact/message';

const headers = () => ({Authorization: `Bearer ${localStorage.getItem('userToken')}`, 'App-Network': localStorage.getItem('networkId')});

export const RecruitmentApplicationListComponent = ({t}) => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const recruitmentId = params.get('recruitmentId');
  const scope = params.get('type') || 'all';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState({first: 0, rows: 10});
  const [revision, setRevision] = useState(0);
  const [creating, setCreating] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setPage(p => ({...p, first: 0})); }, [recruitmentId, scope]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    const query = {scope, page: page.first / page.rows + 1, item_per_page: page.rows, sort: 'id', direction: 'desc'};
    if (recruitmentId) query.recruitment_id = recruitmentId;
    axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/recruitment-application`, {headers: headers(), params: query})
      .then(({data}) => { if (active) { setItems(data.data.items); setTotal(data.data.total_item_count); } })
      .catch(e => { if (active) window.App.toastShow('error', t('error'), e.toString(), '', e.response); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [recruitmentId, scope, page, revision, t]);

  const searchCandidates = async ({query}) => {
    try {
      const {data} = await axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/candidate`, {headers: headers(), params: {
        item_per_page: 30, 'search[name][operator]': 'like', 'search[name][value]': `*${query}*`
      }});
      setSuggestions(data.data.items);
    } catch (e) { setError(t('error')); }
  };
  const create = async () => {
    setSaving(true); setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_HOST}/api/v2/recruitment-application`, {candidate: candidate.id, recruitment: Number(recruitmentId)}, {headers: headers()});
      setCreating(false); setCandidate(null); setRevision(v => v + 1);
      setPage(p => ({...p, first: 0}));
      const query = new URLSearchParams(location.search);
      query.set('type', 'new');
      history.push(`${location.pathname}?${query}`);
    } catch (e) { setError(e.response?.data?.message || e.response?.data?.detail || t('error')); }
    finally { setSaving(false); }
  };
  const showEvents = row => {
    const query = new URLSearchParams(location.search);
    query.set('applicationId', row.id);
    history.push(`${location.pathname}?${query}`);
  };

  return <>
    <div className="p-d-flex p-jc-between p-ai-center p-mb-3">
      <span>{t('recruitmentOccasionsHint')}</span>
      {recruitmentId && (localStorage.getItem('roles') || '').includes('role_recruitment_write') &&
        <Button icon="pi pi-plus" label={t('recruitmentNewApplication')} onClick={() => {setCreating(true); setError('');}}/>}
    </div>
    <Button className="p-button-text p-mb-2" label={t('recruitmentHistoricalEvents')} onClick={() => {
      const query = new URLSearchParams(location.search); query.set('unresolved', '1');
      history.push(`${location.pathname}?${query}`);
    }}/>
    <DataTable value={items} dataKey="id" loading={loading} lazy paginator first={page.first} rows={page.rows}
      totalRecords={total} onPage={e => setPage({first: e.first, rows: e.rows})} rowsPerPageOptions={[10, 25, 50, 100]}
      responsiveLayout="scroll" stripedRows emptyMessage={t('noResults')}>
      <Column field="id" header={t('recruitmentApplicationId')}/>
      <Column field="candidate.name" header={t('columnCandidate')}/>
      <Column field="candidate.email" header={t('columnEmail')}/>
      <Column header={t('recruitmentApplicationDate')} body={row => window.App.formatDateValue(row.created_at)}/>
      <Column field="recruitment.name" header={t('recruitment')}/>
      <Column field="recruitment_campaign.name" header={t('campaign')}/>
      <Column header={t('source')} body={row => t(`recruitmentApplicationSource.${row.source}`, {defaultValue: row.source})}/>
      <Column body={row => <Button className="p-button-text" icon="pi pi-eye" label={t('recruitmentApplicationEvents')} onClick={() => showEvents(row)}/>}/>
    </DataTable>
    <Dialog visible={creating} header={t('recruitmentNewApplication')} onHide={() => {if (!saving) setCreating(false);}} style={{width: '36rem', maxWidth: '95vw'}}>
      <p>{t('recruitmentNewApplicationHint')}</p>
      <label htmlFor="application-candidate">{t('columnCandidate')}</label>
      <AutoComplete inputId="application-candidate" className="p-d-block p-mt-2" value={candidate} suggestions={suggestions}
        field="name" forceSelection completeMethod={searchCandidates} onChange={e => setCandidate(e.value)}
        itemTemplate={item => <span>{item.name} — {item.email || `#${item.id}`}</span>}/>
      {error && <Message severity="error" text={error}/>}
      <div className="p-text-right p-mt-3"><Button label={t('save')} loading={saving} disabled={!candidate?.id || saving} onClick={create}/></div>
    </Dialog>
  </>;
};

export default withTranslation('common')(RecruitmentApplicationListComponent);
